// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setUserFields from "@insite/client-framework/Store/Pages/UserSetup/Handlers/SetUserFields";
import { getCurrentEditingUser } from "@insite/client-framework/Store/Pages/UserSetup/UserSetupSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import Checkbox from "@insite/mobius/Checkbox";
import CheckboxGroup from "@insite/mobius/CheckboxGroup";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { formGridProps } from "@store/Themes/common/props/form";
import constants from "@store/constants";
import setProductSetsIds from "@store/Framework/Store/Store/Components/UserSetupProductSets/Handlers/SetProductSetsIds";
import { UserProfileProductSet } from "@store/Framework/Types/ApiModels";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    editingUser: getCurrentEditingUser(state),
});

const mapDispatchToProps = {
    setUserFields,
    setProductSetsIds,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface UserSetupProductSetsStyles {
    wrapper?: InjectableCss;
    title?: TypographyPresentationProps;
    hintText?: TypographyPresentationProps;
    container?: GridContainerProps;
    gridItem?: GridItemProps;
}

export const userSetupProductSetsStyles: UserSetupProductSetsStyles = {
    wrapper: {
        css: css`
            padding-bottom: ${pxToRem(15)};
        `,
    },
    title: formGridProps.title,
    container: formGridProps.gridContainer,
    gridItem: { width: [12, 6, 6, 6, 6] },
};

const styles = userSetupProductSetsStyles;

const UserSetupProductSets = ({ editingUser, setProductSetsIds }: Props) => {
    const [customerCatalogs, setCustomerCatalogs] = useState(
        JSON.parse(editingUser?.properties.userProfileProductSets ?? "[]") as UserProfileProductSet[],
    );

    const [selectedIds, setSelectedIds] = useState<string[]>(
        customerCatalogs.filter(catalog => catalog.isActive).map(catalog => catalog.id),
    );

    useEffect(() => {
        const parsedSets = JSON.parse(
            editingUser?.properties.userProfileProductSets ?? "[]",
        ) as UserProfileProductSet[];
        setCustomerCatalogs(parsedSets);
        const fetchedSelectedIds = parsedSets.filter(catalog => catalog.isActive).map(catalog => catalog.id);
        setSelectedIds(fetchedSelectedIds);
    }, [editingUser?.properties.userProfileProductSets]);

    useEffect(() => {
        setProductSetsIds({ setsIds: selectedIds });
    }, [selectedIds]);

    const checkProductSet = (id: string) => {
        setSelectedIds(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));
    };

    if (!editingUser || !customerCatalogs || customerCatalogs.length === 0) {
        return null;
    }

    return (
        <StyledWrapper {...styles.wrapper}>
            <Typography {...styles.title}>{translate("Product Sets")}</Typography>
            {editingUser.role === constants.userRole.administrator ? (
                <Typography {...styles.hintText}>
                    {translate("Users with the Administrator role are automatically eligible for all product sets.")}
                </Typography>
            ) : (
                <GridContainer {...styles.container}>
                    <GridItem {...styles.gridItem}>
                        <CheckboxGroup>
                            {customerCatalogs.map(customerCatalog => (
                                <Checkbox
                                    key={customerCatalog.id}
                                    onChange={() => checkProductSet(customerCatalog.id)}
                                    checked={selectedIds.includes(customerCatalog.id)}
                                >
                                    {customerCatalog.displayName}
                                </Checkbox>
                            ))}
                        </CheckboxGroup>
                    </GridItem>
                </GridContainer>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(UserSetupProductSets),
    definition: {
        group: "User Setup",
        displayName: "User Product Sets",
        allowedContexts: ["UserSetupPage"],
    },
};

export default widgetModule;
